/* ================================================================
   authService.ts — email/password auth via backend API.
   Ported from juristab-frontend-app services/authService.js, adapted to the
   fetch-based api wrapper (ApiError carries .status / .data).
   ================================================================ */

import { apiPost, ApiError } from './api';
import { AUTH_ENDPOINTS } from '../config/authConfig';
import { validatePasswordComplexity, ValidationResult } from '../utils/passwordValidation';

/**
 * Fold an email to the one spelling Cognito is keyed on.
 *
 * The user pool is case-sensitive, so `SAYSANDIP@gmail.com` and
 * `saysandip@gmail.com` are two different accounts. Sending the raw input
 * through is how one person ended up with both. The backend normalises too;
 * doing it here as well keeps the address shown back to the user identical to
 * the one the code was actually sent to.
 */
export function normaliseEmail(email: string): string {
  return (email || '').trim().toLowerCase();
}

/** A signup blocked because the address is already taken. */
export class SignupConflictError extends Error {
  /** 'confirmed' — a usable account exists. 'unconfirmed' — signup never finished. */
  state: 'confirmed' | 'unconfirmed' | 'exists';
  /** True when the backend already sent a fresh verification code. */
  codeResent: boolean;
  constructor(message: string, state: SignupConflictError['state'], codeResent: boolean) {
    super(message);
    this.name = 'SignupConflictError';
    this.state = state;
    this.codeResent = codeResent;
  }
}

export interface AuthTokens {
  token: string;
  idToken: string | null;
  refreshToken: string | null;
}

/** Parse a 422 validation error body into a single readable string. */
export function parseValidationError(errData: any): string {
  if (!errData?.detail || !Array.isArray(errData.detail)) {
    return 'Validation failed.';
  }
  const messages = errData.detail
    .map((d: any) => (d && typeof d.msg === 'string' ? d.msg : ''))
    .filter(Boolean);
  return messages.length ? messages.join(', ') : 'Validation failed.';
}

/** Validate signup password: min 8 chars, one uppercase, one number, one special. */
export function validateSignupPassword(password: string): ValidationResult {
  return validatePasswordComplexity(password);
}

function asApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError((err as Error)?.message || 'Request failed.');
}

function throwAuthError(err: unknown): never {
  const e = asApiError(err);
  const status = e.status;
  const errData = e.data;
  const detail = errData?.detail;
  if (status === 401 || status === 403) {
    const message = typeof detail === 'string' ? detail : detail?.message ?? 'Request denied.';
    throw new Error(message);
  }
  if (status === 422 && errData) {
    throw new Error(parseValidationError(errData));
  }
  throw new Error(
    (errData && typeof errData.message === 'string' && errData.message) ||
      (typeof detail === 'string' && detail) ||
      // `detail` is an object on endpoints that return a machine-readable
      // state alongside the prose (signup's 409). Without this the useful
      // message is dropped and the user sees a generic failure.
      (detail && typeof detail.message === 'string' && detail.message) ||
      e.message ||
      'Request failed. Please try again.'
  );
}

/** Normalise a token response that may be a bare string, a quoted string, or an object. */
function coerceTokenData(data: any): any {
  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
      try {
        return JSON.parse(trimmed);
      } catch {
        return trimmed;
      }
    }
    return trimmed;
  }
  return data;
}

/** Login with email and password. */
export async function loginWithEmail({ email, password }: { email: string; password: string }): Promise<AuthTokens> {
  try {
    const { data: raw } = await apiPost(AUTH_ENDPOINTS.login, { email: normaliseEmail(email), password });
    const data = coerceTokenData(raw);
    const token =
      typeof data === 'string'
        ? data
        : data?.tokens?.AccessToken ?? data?.token ?? data?.access_token ?? null;
    if (!token) {
      throw new Error('Login failed. Invalid response from server.');
    }
    const idToken = typeof data === 'string' ? null : data?.tokens?.IdToken ?? null;
    const refreshToken = typeof data === 'string' ? null : data?.tokens?.RefreshToken ?? null;
    return { token, idToken, refreshToken };
  } catch (err) {
    const e = asApiError(err);
    const status = e.status;
    const errData = e.data;
    const detail = errData?.detail;
    if (status === 401 || status === 403) {
      const message = typeof detail === 'string' ? detail : detail?.message ?? 'Invalid email or password.';
      throw new Error(message);
    }
    if (status === 422 && errData) {
      throw new Error(parseValidationError(errData));
    }
    // A thrown Error from the !token guard above lands here with no status.
    if (status === undefined && err instanceof Error && !(err instanceof ApiError)) {
      throw err;
    }
    throw new Error(
      (errData && typeof errData.message === 'string' && errData.message) ||
        (typeof detail === 'string' && detail) ||
        e.message ||
        'Login failed. Please try again.'
    );
  }
}

/** Signup with email and password. Does not return a token; user must verify email. */
export async function signupWithEmail({ email, password }: { email: string; password: string }): Promise<{
  message: string;
  user?: any;
  next_step?: string;
}> {
  try {
    const { data } = await apiPost(AUTH_ENDPOINTS.signup, { email: normaliseEmail(email), password });
    const d = data || {};
    return {
      message: d.message || 'A verification code has been sent to your email.',
      user: d.user,
      next_step: d.next_step,
    };
  } catch (err) {
    const e = asApiError(err);
    // 409 means the address is taken, and the two ways it can be taken need
    // opposite things from the user: an existing account wants a sign-in or a
    // password reset, an unfinished signup wants the verification code. Pass
    // the state up so the UI can send them to the right place instead of
    // offering a password reset Cognito will refuse.
    if (e.status === 409) {
      const d = e.data?.detail;
      const message =
        (d && typeof d.message === 'string' && d.message) ||
        (typeof d === 'string' && d) ||
        'An account with this email already exists.';
      const state = (d && d.state) || 'exists';
      throw new SignupConflictError(message, state, Boolean(d && d.code_resent));
    }
    throwAuthError(err);
  }
}

export interface VerifyEmailResult {
  message?: string;
  token?: string;
  idToken?: string | null;
  refreshToken?: string | null;
  user?: any;
  [key: string]: any;
}

/** Verify email with 6-digit code. Success may return token(s) or only a message. */
export async function verifyEmail({ email, code }: { email: string; code: string }): Promise<VerifyEmailResult> {
  try {
    const { data } = await apiPost(AUTH_ENDPOINTS.verifyEmail, { email: normaliseEmail(email), confirmation_code: code });
    if (typeof data === 'string') {
      const trimmed = data.trim();
      if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
        try {
          return { token: JSON.parse(trimmed) };
        } catch {
          return { token: trimmed };
        }
      }
      return { token: trimmed };
    }
    const rawData = data || {};
    const token = rawData.tokens?.AccessToken ?? rawData.token ?? rawData.access_token;
    const idToken = rawData.tokens?.IdToken ?? null;
    const refreshToken = rawData.tokens?.RefreshToken ?? null;
    return token !== undefined ? { ...rawData, token, idToken, refreshToken } : rawData;
  } catch (err) {
    throwAuthError(err);
  }
}

/** Resend the email verification code. */
export async function resendVerificationCode({ email }: { email: string }): Promise<{ message?: string }> {
  try {
    const { data } = await apiPost(AUTH_ENDPOINTS.resendVerify, { email: normaliseEmail(email) });
    return data || { message: 'Code sent.' };
  } catch (err) {
    throwAuthError(err);
  }
}

/** Request a password reset code. */
export async function requestForgotPassword({ email }: { email: string }): Promise<{ message?: string }> {
  try {
    const { data } = await apiPost(AUTH_ENDPOINTS.forgotPassword, { email: normaliseEmail(email) });
    return data || { message: 'If an account exists, a verification code has been sent.' };
  } catch (err) {
    throwAuthError(err);
  }
}

/** Confirm a password reset with the code and a new password. */
export async function confirmForgotPassword({
  email,
  code,
  newPassword,
}: {
  email: string;
  code: string;
  newPassword: string;
}): Promise<{ message?: string }> {
  try {
    const { data } = await apiPost(AUTH_ENDPOINTS.confirmForgotPassword, {
      email: normaliseEmail(email),
      confirmation_code: code.trim(),
      new_password: newPassword,
    });
    return data || { message: 'Password has been reset.' };
  } catch (err) {
    throwAuthError(err);
  }
}
